import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AppLayout } from './components/AppLayout.js'
import { AuthGuard } from './components/AuthGuard.js'
import { ErrorBoundary } from './components/ErrorBoundary.js'
import { ConsentProvider, CookieBanner } from '@saas/consent'
import { RoleContext, useRoleProvider } from './hooks/useRole.js'
import { QuickNotes } from './components/QuickNotes.js'
import { KeyboardShortcuts } from './components/KeyboardShortcuts.js'
import { OfflineBanner } from './components/OfflineBanner.js'
import { MandatVoiceProvider } from './context/index.js'

// ── Lazy-loaded pages (UX-PERF-04/05: code splitting by route) ──
const DashboardPage = lazy(() => import('./pages/DashboardPage.js').then(m => ({ default: m.DashboardPage })))
const ContactsPage = lazy(() => import('./pages/ContactsPage.js').then(m => ({ default: m.ContactsPage })))
const MandatsPage = lazy(() => import('./pages/MandatsPage.js').then(m => ({ default: m.MandatsPage })))
const PipelinePage = lazy(() => import('./pages/PipelinePage.js').then(m => ({ default: m.PipelinePage })))
const PortalPage = lazy(() => import('./pages/PortalPage.js').then(m => ({ default: m.PortalPage })))
const AriaPage = lazy(() => import('./pages/AriaPage.js').then(m => ({ default: m.AriaPage })))
const MailboxPage = lazy(() => import('./pages/MailboxPage.js').then(m => ({ default: m.MailboxPage })))
const DocumentsPage = lazy(() => import('./pages/DocumentsPage.js').then(m => ({ default: m.DocumentsPage })))
const AdminPage = lazy(() => import('./pages/AdminPage.js').then(m => ({ default: m.AdminPage })))
const SettingsPage = lazy(() => import('./pages/SettingsPage.js').then(m => ({ default: m.SettingsPage })))
const LandingPage = lazy(() => import('./pages/LandingPage.js').then(m => ({ default: m.LandingPage })))
const LoginPage = lazy(() => import('./pages/LoginPage.js').then(m => ({ default: m.LoginPage })))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.js').then(m => ({ default: m.NotFoundPage })))
// ── Proprio Phase 2 ──
const ProprioPage = lazy(() => import('./pages/proprio/ProprioPage.js').then(m => ({ default: m.ProprioPage })))
const BiensPage = lazy(() => import('./pages/proprio/BiensPage.js').then(m => ({ default: m.BiensPage })))
const BailDetailPage = lazy(() => import('./pages/proprio/BailDetailPage.js').then(m => ({ default: m.BailDetailPage })))
const LoyersPage = lazy(() => import('./pages/proprio/LoyersPage.js').then(m => ({ default: m.LoyersPage })))
const EdlPage = lazy(() => import('./pages/proprio/EdlPage.js').then(m => ({ default: m.EdlPage })))
const IndexationPage = lazy(() => import('./pages/proprio/IndexationPage.js').then(m => ({ default: m.IndexationPage })))
const SignalementsPage = lazy(() => import('./pages/proprio/SignalementsPage.js').then(m => ({ default: m.SignalementsPage })))
const PreavisPage = lazy(() => import('./pages/proprio/PreavisPage.js').then(m => ({ default: m.PreavisPage })))
const LocatairePortalPage = lazy(() => import('./pages/proprio/LocatairePortalPage.js').then(m => ({ default: m.LocatairePortalPage })))
// ── Phase 4 — Budget (legacy) ──
const BudgetPage = lazy(() => import('./pages/BudgetPage.js').then(m => ({ default: m.BudgetPage })))
// ── Phase 4 — Budget Finance ──
const BudgetLayout = lazy(() => import('./pages/budget/BudgetLayout.js').then(m => ({ default: m.BudgetLayout })))
const BudgetSynthesePage = lazy(() => import('./pages/budget/BudgetSynthesePage.js'))
const BudgetPersoPage = lazy(() => import('./pages/budget/BudgetPersoPage.js'))
const BudgetProPage = lazy(() => import('./pages/budget/BudgetProPage.js'))
const BudgetPatrimoinePage = lazy(() => import('./pages/budget/BudgetPatrimoinePage.js'))
const BudgetTransactionsPage = lazy(() => import('./pages/budget/BudgetTransactionsPage.js'))
const BudgetReconciliationPage = lazy(() => import('./pages/budget/BudgetReconciliationPage.js'))
const BudgetAriaPage = lazy(() => import('./pages/budget/BudgetAriaPage.js'))
// ── Phase 5 — Invest ──
const InvestPage = lazy(() => import('./pages/InvestPage.js').then(m => ({ default: m.InvestPage })))
const InvestLayout = lazy(() => import('./pages/invest/InvestLayout.js').then(m => ({ default: m.InvestLayout })))
const SimulateurLocatifPage = lazy(() => import('./pages/invest/SimulateurLocatifPage.js'))
const SimulateurCreditPage = lazy(() => import('./pages/invest/SimulateurCreditPage.js'))
const BenchmarkPage = lazy(() => import('./pages/invest/BenchmarkPage.js'))
const FiscalPage = lazy(() => import('./pages/invest/FiscalPage.js'))
// ── Phase 6 — Syndic ──
const SyndicPage = lazy(() => import('./pages/SyndicPage.js').then(m => ({ default: m.SyndicPage })))
// ── Phase 7 — Notaire ──
const NotairePage = lazy(() => import('./pages/NotairePage.js').then(m => ({ default: m.NotairePage })))
// ── Agenda & Tasks (F22, F17) ──
const AgendaPage = lazy(() => import('./pages/AgendaPage.js').then(m => ({ default: m.AgendaPage })))
const TasksPage = lazy(() => import('./pages/TasksPage.js').then(m => ({ default: m.TasksPage })))
// ── Templates & Integrations ──
const TemplatesPage = lazy(() => import('./pages/TemplatesPage.js').then(m => ({ default: m.TemplatesPage })))
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage.js').then(m => ({ default: m.IntegrationsPage })))
// ── UX terrain (F23-F32) ──
const OnboardingPage = lazy(() => import('./pages/OnboardingPage.js').then(m => ({ default: m.OnboardingPage })))
const GlossairePage = lazy(() => import('./pages/GlossairePage.js').then(m => ({ default: m.GlossairePage })))
// ── AML/KYC (F176-F180) ──
const AmlRegistrePage = lazy(() => import('./pages/AmlRegistrePage.js').then(m => ({ default: m.AmlRegistrePage })))
// ── UX terrain (F33-F42) ──
const ComparePage = lazy(() => import('./pages/ComparePage.js').then(m => ({ default: m.ComparePage })))
const PresentationPage = lazy(() => import('./pages/PresentationPage.js').then(m => ({ default: m.PresentationPage })))
const DroitsEnregistrementPage = lazy(() => import('./pages/DroitsEnregistrementPage.js').then(m => ({ default: m.DroitsEnregistrementPage })))
const SignaturePage = lazy(() => import('./pages/SignaturePage.js').then(m => ({ default: m.SignaturePage })))
// ── Voice Intelligence ──
const VoiceNotesPage = lazy(() => import('./pages/VoiceNotesPage.js').then(m => ({ default: m.VoiceNotesPage })))
const InspectionPage = lazy(() => import('./pages/InspectionPage.js').then(m => ({ default: m.InspectionPage })))

// ── Support ──
const SupportTenantPickerPage = lazy(() => import('./pages/SupportTenantPickerPage.js').then(m => ({ default: m.SupportTenantPickerPage })))

const CONSENT_CONFIG = { appId: 'patrimia', version: '1' }
const CONSENT_LABELS = {
  title: 'Respect de votre vie privée',
  description: 'Nous utilisons des cookies pour améliorer votre expérience. Vous pouvez choisir les catégories à autoriser.',
  acceptAll: 'Tout accepter',
  rejectAll: 'Tout refuser',
  manage: 'Gérer mes préférences',
  save: 'Enregistrer',
  necessary: 'Nécessaires',
  analytics: 'Analytiques',
  marketing: 'Marketing',
  preferences: 'Préférences',
}

export function App() {
  const roleCtx = useRoleProvider()
  return (
    <ErrorBoundary>
    <RoleContext.Provider value={roleCtx}>
    <ConsentProvider config={CONSENT_CONFIG}>
    <BrowserRouter>
    <MandatVoiceProvider>
      <Suspense fallback={<div className="flex h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--color-primary)] border-t-transparent" /></div>}>
      <Routes>
        {/* Pages publiques */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />

        {/* Pages authentifiées — AuthGuard + AppLayout */}
        <Route path="/dashboard" element={<AuthGuard><AppLayout><DashboardPage /></AppLayout></AuthGuard>} />

        {/* Sprint 1 */}
        <Route path="/contacts" element={<AuthGuard><AppLayout><ContactsPage /></AppLayout></AuthGuard>} />
        <Route path="/contacts/:id" element={<AuthGuard><AppLayout><ContactsPage /></AppLayout></AuthGuard>} />

        {/* Sprint 2 */}
        <Route path="/mandats" element={<AuthGuard><AppLayout><MandatsPage /></AppLayout></AuthGuard>} />
        <Route path="/mandats/new" element={<AuthGuard><AppLayout><MandatsPage /></AppLayout></AuthGuard>} />
        <Route path="/mandats/:id" element={<AuthGuard><AppLayout><MandatsPage /></AppLayout></AuthGuard>} />

        {/* Sprint 3 */}
        <Route path="/pipeline" element={<AuthGuard><AppLayout><PipelinePage /></AppLayout></AuthGuard>} />

        {/* Sprint 7-8 */}
        <Route path="/aria" element={<AuthGuard><AppLayout><AriaPage /></AppLayout></AuthGuard>} />

        {/* Sprint 9 */}
        <Route path="/mailbox" element={<AuthGuard><AppLayout><MailboxPage /></AppLayout></AuthGuard>} />

        {/* Sprint 10 */}
        <Route path="/documents" element={<AuthGuard><AppLayout><DocumentsPage /></AppLayout></AuthGuard>} />

        {/* Sprint 12 */}
        <Route path="/admin" element={<AuthGuard><AppLayout><AdminPage /></AppLayout></AuthGuard>} />

        {/* Sprint 13 */}
        <Route path="/settings" element={<AuthGuard><AppLayout><SettingsPage /></AppLayout></AuthGuard>} />

        {/* Portail client CRM (Sprint 6 — layout different, pas d'AppLayout) */}
        <Route path="/portail/client/*" element={<PortalPage />} />
        {/* Portail locataire Proprio (Phase 2) */}
        <Route path="/portail/locataire" element={<LocatairePortalPage />} />

        {/* ── Proprio Phase 2 ── */}
        <Route path="/proprio" element={<AuthGuard><AppLayout><ProprioPage /></AppLayout></AuthGuard>} />
        <Route path="/proprio/biens" element={<AuthGuard><AppLayout><BiensPage /></AppLayout></AuthGuard>} />
        <Route path="/proprio/baux" element={<AuthGuard><AppLayout><BiensPage /></AppLayout></AuthGuard>} />
        <Route path="/proprio/biens/:id" element={<AuthGuard><AppLayout><BailDetailPage /></AppLayout></AuthGuard>} />
        <Route path="/proprio/loyers" element={<AuthGuard><AppLayout><LoyersPage /></AppLayout></AuthGuard>} />
        <Route path="/proprio/edl" element={<AuthGuard><AppLayout><EdlPage /></AppLayout></AuthGuard>} />
        <Route path="/proprio/indexation" element={<AuthGuard><AppLayout><IndexationPage /></AppLayout></AuthGuard>} />
        <Route path="/proprio/signalements" element={<AuthGuard><AppLayout><SignalementsPage /></AppLayout></AuthGuard>} />
        <Route path="/proprio/preavis" element={<AuthGuard><AppLayout><PreavisPage /></AppLayout></AuthGuard>} />

        {/* ── Phase 4 — Budget (legacy charges bail) ── */}
        <Route path="/budget-charges" element={<AuthGuard><AppLayout><BudgetPage /></AppLayout></AuthGuard>} />

        {/* ── Phase 4 — Budget Finance ── */}
        <Route path="/budget" element={<AuthGuard><AppLayout><BudgetLayout /></AppLayout></AuthGuard>}>
          <Route index element={<BudgetSynthesePage />} />
          <Route path="perso" element={<BudgetPersoPage />} />
          <Route path="pro" element={<BudgetProPage />} />
          <Route path="patrimoine" element={<BudgetPatrimoinePage />} />
          <Route path="transactions" element={<BudgetTransactionsPage />} />
          <Route path="reconciliation" element={<BudgetReconciliationPage />} />
          <Route path="aria" element={<BudgetAriaPage />} />
        </Route>

        {/* ── Phase 5 — Invest ── */}
        <Route path="/invest" element={<AuthGuard><AppLayout><InvestLayout /></AppLayout></AuthGuard>}>
          <Route index element={<InvestPage />} />
          <Route path="simulateur-locatif" element={<SimulateurLocatifPage />} />
          <Route path="simulateur-credit" element={<SimulateurCreditPage />} />
          <Route path="benchmark" element={<BenchmarkPage />} />
          <Route path="fiscal" element={<FiscalPage />} />
        </Route>

        {/* ── Phase 6 — Syndic ── */}
        <Route path="/syndic" element={<AuthGuard><AppLayout><SyndicPage /></AppLayout></AuthGuard>} />
        <Route path="/syndic/ag" element={<AuthGuard><AppLayout><SyndicPage /></AppLayout></AuthGuard>} />

        {/* ── Agenda & Tasks (F22, F17) ── */}
        <Route path="/agenda" element={<AuthGuard><AppLayout><AgendaPage /></AppLayout></AuthGuard>} />
        <Route path="/tasks" element={<AuthGuard><AppLayout><TasksPage /></AppLayout></AuthGuard>} />

        {/* ── UX terrain (F23-F32) ── */}
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/glossaire" element={<AuthGuard><AppLayout><GlossairePage /></AppLayout></AuthGuard>} />

        {/* ── UX terrain (F33-F42) ── */}
        <Route path="/mandats/comparer" element={<AuthGuard><AppLayout><ComparePage /></AppLayout></AuthGuard>} />
        <Route path="/presentation/:id" element={<PresentationPage />} />
        <Route path="/droits-enregistrement" element={<AuthGuard><AppLayout><DroitsEnregistrementPage /></AppLayout></AuthGuard>} />
        <Route path="/signature/:id" element={<AuthGuard><AppLayout><SignaturePage /></AppLayout></AuthGuard>} />

        {/* ── Voice Intelligence ── */}
        <Route path="/voice-notes" element={<AuthGuard><AppLayout><VoiceNotesPage /></AppLayout></AuthGuard>} />
        <Route path="/inspections" element={<AuthGuard><AppLayout><InspectionPage /></AppLayout></AuthGuard>} />
        <Route path="/inspections/:id" element={<AuthGuard><AppLayout><InspectionPage /></AppLayout></AuthGuard>} />

        {/* ── AML/KYC (F176-F180) ── */}
        <Route path="/aml" element={<AuthGuard><AppLayout><AmlRegistrePage /></AppLayout></AuthGuard>} />

        {/* ── Templates & Integrations ── */}
        <Route path="/templates" element={<AuthGuard><AppLayout><TemplatesPage /></AppLayout></AuthGuard>} />
        <Route path="/integrations" element={<AuthGuard><AppLayout><IntegrationsPage /></AppLayout></AuthGuard>} />

        {/* ── Phase 7 — Notaire ── */}
        <Route path="/notaire" element={<AuthGuard><AppLayout><NotairePage /></AppLayout></AuthGuard>} />
        <Route path="/notaire/checklists" element={<AuthGuard><AppLayout><NotairePage /></AppLayout></AuthGuard>} />

        {/* ── Support ── */}
        <Route path="/support/tenant-picker" element={<SupportTenantPickerPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      <CookieBanner labels={CONSENT_LABELS} />
      <QuickNotes />
      <KeyboardShortcuts />
      <OfflineBanner />
    </MandatVoiceProvider>
    </BrowserRouter>
    </ConsentProvider>
    </RoleContext.Provider>
    </ErrorBoundary>
  )
}
